import { createMiddleware, createServerFn } from "@tanstack/react-start"
import { redirect } from "@tanstack/react-router"
import { z } from "zod"

import {
  DEMO_ACCOUNTS,
  DEMO_ACTIVITIES,
  DEMO_ADMIN_OVERVIEW,
  DEMO_SCENES,
  SUPPORT_TOPICS,
} from "@/lib/demo-data"
import {
  createDemoOrder,
  filterDemoScenes,
  visibleOrdersForViewer,
} from "@/lib/demo-workflow"
import type { DownloadRecord, Order } from "@/lib/domain"
import { ORDER_STATUS_TRANSITIONS } from "@/lib/domain"

import {
  demoStateFor,
  mutateDemoState,
  resetSessionDemoState,
} from "./demo-state.server"
import {
  currentDemoSession,
  currentViewer,
  signInDemo,
  signOutDemo,
} from "./session.server"

const authMiddleware = createMiddleware({ type: "function" }).server(
  async ({ next }) => {
    const demoSession = await currentDemoSession()

    if (!demoSession) {
      throw redirect({ to: "/login" })
    }

    return next({ context: demoSession })
  }
)

const adminMiddleware = createMiddleware({ type: "function" })
  .middleware([authMiddleware])
  .server(async ({ next, context }) => {
    if (context.viewer.role !== "admin") {
      throw redirect({ to: "/portal" })
    }

    return next({ context })
  })

const sceneFiltersSchema = z.object({
  query: z.string().trim().max(120).optional(),
  type: z.enum(["Any", "OPTICAL", "SAR"]).optional(),
  satellite: z.string().trim().max(80).optional(),
  maxCloudCover: z.number().min(0).max(100).optional(),
  maxResolution: z.number().min(0.1).max(100).optional(),
})

const orderStatusUpdateSchema = z
  .object({
    orderId: z.string().trim().min(1).max(120),
    status: z.enum([
      "submitted",
      "review",
      "approved",
      "ready",
      "completed",
      "rejected",
    ]),
    reason: z.string().trim().max(400).optional(),
  })
  .superRefine((value, context) => {
    if (
      value.status === "rejected" &&
      (!value.reason || value.reason.length < 12)
    ) {
      context.addIssue({
        code: "custom",
        path: ["reason"],
        message: "Add a revision reason of at least 12 characters.",
      })
    }
  })

export const getPublicDataFn = createServerFn({ method: "GET" }).handler(
  async () => ({
    viewer: await currentViewer(),
    accounts: DEMO_ACCOUNTS.map(
      ({ password: _password, ...account }) => account
    ),
    featuredScenes: DEMO_SCENES.slice(0, 3),
  })
)

export const getViewerFn = createServerFn({ method: "GET" }).handler(async () =>
  currentViewer()
)

export const loginFn = createServerFn({ method: "POST" })
  .validator(
    z.object({
      email: z.string().trim().toLowerCase().email().max(254),
      password: z.string().min(1).max(120),
    })
  )
  .handler(async ({ data }) => {
    const viewer = await signInDemo(data.email, data.password)

    if (!viewer) {
      throw new Error(
        "Those demo credentials do not match. Use one of the accounts shown on this page."
      )
    }

    return viewer
  })

export const logoutFn = createServerFn({ method: "POST" }).handler(async () => {
  await signOutDemo()
  return { ok: true as const }
})

export const getDashboardFn = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .handler(async ({ context }) => {
    const state = await demoStateFor(context.stateId)
    const viewerOrders = state.orders.filter(
      (order) =>
        context.viewer.role === "admin" ||
        order.requester === context.viewer.name
    )
    const readyDownloads = state.downloads.filter(
      (download) => download.status === "available"
    )

    return {
      viewer: context.viewer,
      statistics: {
        newOrders: viewerOrders.filter((order) =>
          ["submitted", "review"].includes(order.status)
        ).length,
        approvedOrders: viewerOrders.filter((order) =>
          ["approved", "ready"].includes(order.status)
        ).length,
        totalOrders: viewerOrders.length,
        totalDownloads: state.downloads.length,
        availableDownloads: readyDownloads.length,
        remainingFiles: 8 - readyDownloads.length,
        remainingSizeGb:
          25 -
          readyDownloads.reduce(
            (total, download) => total + download.sizeGb,
            0
          ),
      },
      orders: viewerOrders,
      activities: DEMO_ACTIVITIES,
      recommendedScenes: DEMO_SCENES.slice(0, 3),
    }
  })

export const getScenesFn = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .validator(sceneFiltersSchema)
  .handler(async ({ data }) => {
    const scenes = filterDemoScenes(data)

    return {
      scenes,
      total: scenes.length,
      catalogueTotal: DEMO_ADMIN_OVERVIEW.totals.imagery,
      generatedLabel: "Synthetic catalogue · refreshed 27 Jul 2026",
    }
  })

export const getOrdersFn = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .handler(async ({ context }) => {
    const state = await demoStateFor(context.stateId)

    return {
      viewer: context.viewer,
      orders: visibleOrdersForViewer(state, context.viewer),
    }
  })

export const createOrderFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(
    z.object({
      kind: z.enum(["Archive", "New tasking"]),
      title: z.string().trim().min(4).max(120),
      purpose: z.string().trim().min(12).max(800),
      areaLabel: z.string().trim().min(4).max(160),
      sceneIds: z.array(z.string().trim().min(1).max(120)).max(12),
      requestedSatellite: z.string().trim().min(1).max(80),
      dateRange: z.string().trim().min(4).max(100),
      notes: z.string().trim().max(800),
    })
  )
  .handler(async ({ data, context }) => {
    return mutateDemoState(context.stateId, (state) => {
      const result = createDemoOrder(state, data, context.viewer)
      return { state: result.state, result: result.order }
    })
  })

export const getDownloadsFn = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .handler(async ({ context }) => {
    const state = await demoStateFor(context.stateId)

    return {
      downloads: [...state.downloads],
      allocation: {
        fileLimit: 8,
        sizeLimitGb: 25,
        activeFiles: state.downloads.filter(
          (download) => download.status === "available"
        ).length,
        activeSizeGb: state.downloads
          .filter((download) => download.status === "available")
          .reduce((total, download) => total + download.sizeGb, 0),
      },
    }
  })

export const prepareDownloadFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(z.object({ sceneId: z.string().trim().min(1).max(120) }))
  .handler(async ({ data, context }) => {
    const scene = DEMO_SCENES.find((item) => item.id === data.sceneId)

    if (!scene) {
      throw new Error("That synthetic scene could not be found.")
    }

    return mutateDemoState(context.stateId, (state) => {
      const existing = state.downloads.find(
        (item) =>
          item.sceneId === scene.id &&
          ["available", "preparing"].includes(item.status)
      )

      if (existing) {
        return { state, result: existing }
      }

      const record: DownloadRecord = {
        id: `DL-DEMO-${String(state.downloads.length + 101).padStart(4, "0")}`,
        sceneId: scene.id,
        orderId: "DIRECT-DEMO",
        fileName: `${scene.id.replaceAll("-", "_")}_DEMO.tif`,
        sizeGb: scene.fileSizeGb,
        requestedAt: "2026-07-27T06:35:00Z",
        requestedLabel: "Just now",
        status: "preparing",
        expiresLabel: "03 Aug 2026",
      }

      return {
        state: { ...state, downloads: [record, ...state.downloads] },
        result: record,
      }
    })
  })

export const getHelpFn = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .handler(async () => ({
    topics: SUPPORT_TOPICS,
    serviceHours: "Monday–Friday · 08:00–17:00 PHT",
    contact: "support@demo.service-portal.test",
  }))

export const submitFeedbackFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(
    z.object({
      observation: z.string().trim().min(12).max(1200),
      suggestion: z.string().trim().max(1200),
    })
  )
  .handler(async ({ context }) => {
    return mutateDemoState(context.stateId, (state) => ({
      state,
      result: {
        ok: true as const,
        reference: `FDBK-DEMO-${String(100 + state.orders.length).padStart(4, "0")}`,
      },
    }))
  })

export const getAdminOverviewFn = createServerFn({ method: "GET" })
  .middleware([adminMiddleware])
  .handler(async ({ context }) => {
    const state = await demoStateFor(context.stateId)

    return {
      viewer: context.viewer,
      ...DEMO_ADMIN_OVERVIEW,
      totals: {
        ...DEMO_ADMIN_OVERVIEW.totals,
        openOrders: state.orders.filter((order) =>
          ["submitted", "review", "approved"].includes(order.status)
        ).length,
        readyDownloads: state.downloads.filter(
          (download) => download.status === "available"
        ).length,
      },
      recentOrders: state.orders.slice(0, 6),
    }
  })

export const updateOrderStatusFn = createServerFn({ method: "POST" })
  .middleware([adminMiddleware])
  .validator(orderStatusUpdateSchema)
  .handler(async ({ data, context }) => {
    return mutateDemoState(context.stateId, (state) => {
      const existingOrder = state.orders.find(
        (order) => order.id === data.orderId
      )

      if (!existingOrder) {
        throw new Error("That demo order could not be found.")
      }

      if (
        !ORDER_STATUS_TRANSITIONS[existingOrder.status].includes(data.status)
      ) {
        throw new Error(
          `${existingOrder.status} orders cannot move directly to ${data.status}.`
        )
      }

      const updated: Order = {
        ...existingOrder,
        status: data.status,
        updatedLabel: "Just now",
        operatorNote:
          data.status === "rejected" ? data.reason : existingOrder.operatorNote,
      }
      return {
        state: {
          ...state,
          orders: state.orders.map((order) =>
            order.id === updated.id ? updated : order
          ),
        },
        result: updated,
      }
    })
  })

export const resetDemoFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .handler(async ({ context }) => {
    return resetSessionDemoState(context.stateId)
  })
